import React, { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Info } from "lucide-react";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import SeoMeta from "../components/storefront/SeoMeta";
import AdfImage from "../components/storefront/AdfImage";
import StoreLocationsGrid from "../components/storefront/StoreLocationsGrid";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { pickLocale } from "../lib/format";
import { useContactDetails, useSections } from "../hooks/useSiteContent";
import { CONTACT_DETAILS_KEY } from "../lib/siteContent";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { apiErrorMessage } from "../lib/apiErrors";
import { analytics } from "../lib/analytics";

/**
 * The support page in the owner's reference layout: a hero photograph, a rail
 * of the site's help pages on the left, and on the right the ways to reach the
 * atelier — support lines, WhatsApp, an enquiry form, and the boutiques.
 *
 * Facts come from the admin panel (contact-details bag + store locations);
 * the rail links only to pages that exist.
 */
const TOPIC_KEYS = [
  ["order", "contact.topic.order", "Sifariş və çatdırılma"],
  ["product", "contact.topic.product", "Məhsul və ölçü"],
  ["return", "contact.topic.return", "Qaytarma və dəyişdirmə"],
  ["loyalty", "contact.topic.loyalty", "Falcon Society"],
  ["other", "contact.topic.other", "Digər"],
];

const RAIL = [
  ["/contact", "contact.rail.contact", "Əlaqə"],
  ["/falcon-society", "contact.rail.loyalty", "Loyallıq proqramı"],
  ["/terms", "contact.rail.terms", "İstifadəçi razılaşması"],
  ["/delivery", "contact.rail.delivery", "Çatdırılma şərtləri"],
  ["/returns", "contact.rail.returns", "Geri qaytarılma və dəyişdirmə"],
  ["/gift-card", "contact.rail.gift_cards", "Hədiyyə kartları"],
  ["/privacy", "contact.rail.privacy", "Məxfilik siyasəti"],
];

export default function ContactPage() {
  const { locale, t } = useI18n();
  const details = useContactDetails() || {};
  const sections = useSections("contact");
  useDocumentTitle(t("contact.title", "Əlaqə"));

  const [form, setForm] = useState({ topic: "", name: "", email: "", phone: "", message: "", company: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");

  const hours = pickLocale(details.hours, locale) || (typeof details.hours === "string" ? details.hours : "");
  const whatsappDigits = String(details.whatsapp || "").replace(/\D/g, "");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (form.company) return; // honeypot: a filled hidden field is not a person
    if (!form.message.trim()) {
      setErr(t("contact.form.message_required", "Mesajınızı yazın."));
      return;
    }
    if (!form.email.trim() && !form.phone.trim()) {
      setErr(t("contact.form.contact_required", "Cavab üçün e-poçt və ya telefon yazın."));
      return;
    }
    setBusy(true);
    try {
      const topicLabel = TOPIC_KEYS.find(([key]) => key === form.topic);
      await api.post("/concierge/requests", {
        name: form.name.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        /* The topic is prefixed into the message as well as sent as a field,
           so staff sees it even on a backend that predates the column. */
        message: topicLabel
          ? `[${t(topicLabel[1], topicLabel[2])}] ${form.message.trim()}`
          : form.message.trim(),
        topic: form.topic || null,
        source: "contact_page",
        page_url: window.location.href,
      });
      analytics.lead("contact_form");
      setSent(true);
    } catch (ex) {
      setErr(apiErrorMessage(ex, t));
    } finally {
      setBusy(false);
    }
  };

  const contentSections = (sections || []).filter((s) => s.key !== CONTACT_DETAILS_KEY);

  return (
    <div className="bg-white text-[var(--adf-ink)]" data-testid="page-contact">
      <SeoMeta
        title={t("contact.title", "Əlaqə")}
        description={t("contact.intro", "Sifariş, ölçü və ya fərdi tikiş barədə bizimlə əlaqə saxlayın.")}
        locale={locale}
        canonicalPath="/contact"
      />
      <Header />

      {details.hero_url && (
        <div className="h-[300px] w-full overflow-hidden sm:h-[420px]" data-testid="contact-hero">
          <AdfImage source={details.hero_url} alt="" sizes="100vw" className="h-full w-full object-cover" />
        </div>
      )}

      <main id="main" className="adf-container py-12 sm:py-16">
        <div className="grid gap-10 lg:grid-cols-[240px_minmax(0,1fr)] lg:items-start">
          {/* The rail: every help page the site actually has. */}
          <nav aria-label={t("contact.rail.aria", "Dəstək bölmələri")} data-testid="contact-rail">
            <ul className="space-y-1">
              {RAIL.map(([to, key, fallback]) => {
                const active = to === "/contact";
                return (
                  <li key={to}>
                    <Link
                      to={to}
                      aria-current={active ? "page" : undefined}
                      className={`flex min-h-[44px] items-center px-4 text-[12px] uppercase tracking-[0.1em] transition ${
                        active
                          ? "bg-[var(--adf-gray-6,#f2f1ef)] font-medium"
                          : "text-[var(--adf-gray-1)] hover:bg-[var(--adf-gray-6,#f2f1ef)]"
                      }`}
                    >
                      {t(key, fallback)}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div>
            <h1 className="font-serif text-[26px] leading-snug sm:text-[30px]">
              {t("contact.reach_us", "Bizimlə əlaqə saxlayın")}
            </h1>
            <hr className="mt-4 border-[var(--adf-gray-5)]" />

            {/* Support lines on the left, WhatsApp on the right. */}
            <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_200px]" data-testid="contact-details">
              <div className="divide-y divide-[var(--adf-gray-5)] border-y border-[var(--adf-gray-5)]">
                {details.phone && (
                  <div className="grid gap-2 py-4 sm:grid-cols-[minmax(0,1fr)_auto_auto] sm:items-center sm:gap-6">
                    <div className="text-[14px] leading-6 text-[var(--adf-gray-1)]">
                      {t("contact.support.sales", "Onlayn satış və sifarişlərlə bağlı suallar və istəklər üçün:")}
                    </div>
                    {hours && <div className="text-[13px] text-[var(--adf-gray-2)]">{hours}</div>}
                    <a href={`tel:${String(details.phone).replace(/\s/g, "")}`} className="text-[14px] underline underline-offset-4">
                      {details.phone}
                    </a>
                  </div>
                )}
                {details.email && (
                  <div className="grid gap-2 py-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center sm:gap-6">
                    <div className="text-[14px] leading-6 text-[var(--adf-gray-1)]">
                      {t("contact.support.written", "Yazılı müraciətlər, təkliflər və əməkdaşlıq üçün:")}
                    </div>
                    <a href={`mailto:${details.email}`} className="text-[14px] underline underline-offset-4">
                      {details.email}
                    </a>
                  </div>
                )}
              </div>

              {whatsappDigits && (
                <a
                  href={`https://wa.me/${whatsappDigits}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex flex-col items-center justify-center gap-3 border border-[var(--adf-gray-5)] px-6 py-8 text-center transition hover:border-[var(--adf-ink)]"
                  data-testid="contact-whatsapp"
                >
                  <MessageCircle size={34} strokeWidth={1.2} aria-hidden="true" />
                  <span className="text-[13px] leading-5">{t("contact.whatsapp", "Bizə mesaj göndər")}</span>
                </a>
              )}
            </div>

            {/* The enquiry form. */}
            <h2 className="mt-12 font-serif text-[22px] sm:text-[24px]">
              {t("contact.form.title", "Əlaqə forması")}
            </h2>
            <hr className="mt-4 border-[var(--adf-gray-5)]" />

            {sent ? (
              <div className="mt-6 border border-[var(--adf-gray-5)] bg-[var(--adf-gray-6,#f7f6f4)] px-5 py-6 text-[15px] leading-7" data-testid="contact-form-sent">
                {t("contact.form.sent", "Müraciətiniz atelyeyə çatdı. 24 saat ərzində sizinlə əlaqə saxlayacağıq.")}
              </div>
            ) : (
              <form onSubmit={submit} className="mt-6 max-w-[560px]" data-testid="contact-form">
                <label className="block">
                  <span className="text-[13px] text-[var(--adf-gray-1)]">{t("contact.form.topic", "Müraciətin növü")}</span>
                  <select
                    value={form.topic}
                    onChange={(e) => setForm({ ...form, topic: e.target.value })}
                    className="mt-2 h-12 w-full border border-[var(--adf-gray-4)] bg-white px-3 text-[15px] outline-none focus:border-[var(--adf-ink)]"
                    data-testid="contact-topic"
                  >
                    <option value="">{t("contact.form.topic_placeholder", "Müraciətin növünü seçin")}</option>
                    {TOPIC_KEYS.map(([key, i18nKey, fallback]) => (
                      <option key={key} value={key}>{t(i18nKey, fallback)}</option>
                    ))}
                  </select>
                </label>
                <label className="mt-4 block">
                  <span className="text-[13px] text-[var(--adf-gray-1)]">{t("contact.form.name", "Tam ad")}</span>
                  <input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={t("contact.form.name", "Tam ad")}
                    autoComplete="name"
                    className="mt-2 h-12 w-full border border-[var(--adf-gray-4)] bg-white px-3 text-[15px] outline-none placeholder:text-[var(--adf-gray-3)] focus:border-[var(--adf-ink)]"
                    data-testid="contact-name"
                  />
                </label>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-[13px] text-[var(--adf-gray-1)]">Email</span>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="Email"
                      autoComplete="email"
                      className="mt-2 h-12 w-full border border-[var(--adf-gray-4)] bg-white px-3 text-[15px] outline-none placeholder:text-[var(--adf-gray-3)] focus:border-[var(--adf-ink)]"
                      data-testid="contact-email"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[13px] text-[var(--adf-gray-1)]">{t("contact.form.phone", "Mobil nömrə")}</span>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder={t("contact.form.phone", "Mobil nömrə")}
                      autoComplete="tel"
                      className="mt-2 h-12 w-full border border-[var(--adf-gray-4)] bg-white px-3 text-[15px] outline-none placeholder:text-[var(--adf-gray-3)] focus:border-[var(--adf-ink)]"
                      data-testid="contact-phone"
                    />
                  </label>
                </div>
                <label className="mt-4 block">
                  <span className="text-[13px] text-[var(--adf-gray-1)]">{t("contact.form.message", "Mesaj")}</span>
                  <textarea
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder={t("contact.form.message", "Mesaj")}
                    rows={5}
                    className="mt-2 w-full resize-y border border-[var(--adf-gray-4)] bg-white px-3 py-3 text-[15px] outline-none placeholder:text-[var(--adf-gray-3)] focus:border-[var(--adf-ink)]"
                    data-testid="contact-message"
                  />
                </label>
                {/* Honeypot — invisible to people, irresistible to bots. */}
                <input
                  type="text"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute -left-[9999px] h-0 w-0 opacity-0"
                  name="company"
                />
                {err && <div role="alert" className="mt-4 text-sm text-[#8a3b2e]" data-testid="contact-form-error">{err}</div>}
                <button
                  type="submit"
                  disabled={busy}
                  className="mt-6 min-h-[52px] w-full bg-[var(--adf-ink)] text-[15px] font-medium text-white transition hover:opacity-90 disabled:opacity-50"
                  data-testid="contact-submit"
                >
                  {busy ? "…" : t("contact.form.send", "Göndər")}
                </button>
                <p className="mt-4 flex gap-2 text-[13px] leading-6 text-[var(--adf-gray-2)]">
                  <Info size={15} strokeWidth={1.4} className="mt-1 shrink-0" aria-hidden="true" />
                  {t("contact.form.note", "24 saat ərzində cavab verməyi hədəfləyirik, lakin bayramlar, satışlar və xüsusi kampaniyalar kimi gərgin dövrlərdə sizinlə əlaqə saxlamaq bir az daha uzun çəkə bilər.")}
                </p>
              </form>
            )}

            <StoreLocationsGrid className="mt-14 border-t border-[var(--adf-gray-5)] pt-10" />

            {/* Anything the editor has published for this page. */}
            {contentSections.map((section) => {
              const heading = pickLocale(section.title, locale);
              const body = pickLocale(section.body, locale);
              return (
                <section key={section.id} className="mt-12 border-t border-[var(--adf-gray-5)] pt-8" data-testid={`section-${section.key}`}>
                  {heading && <h2 className="font-serif text-[22px] sm:text-[24px]">{heading}</h2>}
                  {body && (
                    <div className="mt-4 space-y-4 text-[15px] leading-7 text-[var(--adf-gray-1)]">
                      {body.split(/\n\s*\n/).map((part) => part.trim()).filter(Boolean).map((part) => (
                        <p key={part.slice(0, 40)} className="whitespace-pre-line">{part}</p>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
